import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';

import { Tab } from '@mui/material';
import {
  DatePicker,
  TimePicker,
} from '@mui/lab';

import useReminderForm from '../../../../hooks/useReminderForm';
import UserContext from '../../../../context/UserContext';
import CoachReminderNotification from '../../../../Model/CoachReminderNotification';

import TabPanel from '../../../components/TabPanel';
import DialogRoundedModal, { ModalBody, ModalActionButton } from '../../../../components/DialogRoundedModal';
import ScheduledMessagesList from './components/ScheduledReminders';
import RecurrenceBlock from './components/RecurrenceBlock';

import {
  StyledTextField,
  StyledTabs,
  StyledTextarea,
} from './styles';

import text from './text.json';

const CoachReminderModal = ({
  isOpen,
  showReminderScheduled,
  onDidDismiss,
  showReminderError,
  selectedSendDate,
  ...modalProps
}) => {
  const [tabSelected, setTabSelected] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { userId } = useContext(UserContext);

  const form = useReminderForm();
  const {
    reminderText, setReminderText,
    sendDate, setSendDate,
    sendTime, setSendTime,
    isRecurring, setIsRecurring,
    interval, setInterval,
    byDay, setByDay,
    endDate, setEndDate,
    timeZone,
    nextFireMoment,
    minTime,
    submitDisabled,
    resolveSendAt,
    reset,
  } = form;

  useEffect(() => {
    if (selectedSendDate) {
      setSendDate(moment(selectedSendDate).local());
    }
  }, [selectedSendDate, setSendDate]);

  const handleReminderSchedule = useCallback(async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      const { rule, sendAt, isPastDate } = resolveSendAt();
      if (sendAt === null) {
        showReminderError();
        return;
      }
      if (isPastDate) {
        showReminderError(text.pastDateError);
        return;
      }
      await CoachReminderNotification.create({
        content: reminderText,
        coach: userId,
        sendAt,
        ...(rule && { recurrence: rule }),
      });
      reset();
      showReminderScheduled();
      setTabSelected(1);
    } catch (error) {
      Sentry.captureException(error, { extra: { userId } });
      showReminderError();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    resolveSendAt,
    reminderText,
    reset,
    showReminderScheduled,
    showReminderError,
    userId,
  ]);

  const onTabChange = useCallback((event, value) => {
    setTabSelected(value);
  }, []);

  const a11yProps = useCallback((index) => ({
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  }), []);

  return (
    <DialogRoundedModal
      open={isOpen}
      onClose={onDidDismiss}
      title={text.coachReminder}
      description={text.subtitle}
      maxWidth="sm"
      fullWidth
      actionButtons={tabSelected === 0 ? (
        <ModalActionButton
          onClick={handleReminderSchedule}
          disabled={submitDisabled || isSubmitting}
        >
          {text.scheduleReminder}
        </ModalActionButton>
      ) : null}
      {...modalProps}
    >
      <StyledTabs value={tabSelected} onChange={onTabChange}>
        <Tab label={text.newReminder} {...a11yProps(0)} />
        <Tab label={text.scheduledReminders} {...a11yProps(1)} />
      </StyledTabs>
      <ModalBody>
        <TabPanel
          index={0}
          value={tabSelected}
        >
          <DatePicker
            label={isRecurring ? text.startDate : text.selectDate}
            value={sendDate}
            onChange={setSendDate}
            minDate={moment().local()}
            renderInput={(params) => (
              <StyledTextField {...params} />
            )}
          />
          <TimePicker
            label={text.selectTime}
            value={sendTime}
            onChange={setSendTime}
            minTime={minTime}
            minutesStep={5}
            renderInput={(params) => (
              <StyledTextField {...params} />
            )}
          />

          <StyledTextarea
            placeholder={text.reminderPlaceholder}
            value={reminderText}
            rows={5}
            onIonChange={(e) => setReminderText(e.detail.value)}
          />

          <RecurrenceBlock
            isRecurring={isRecurring}
            onIsRecurringChange={setIsRecurring}
            interval={interval}
            onIntervalChange={setInterval}
            byDay={byDay}
            onByDayChange={setByDay}
            endDate={endDate}
            onEndDateChange={setEndDate}
            startDate={sendDate}
            timeZone={timeZone}
            nextFireMoment={nextFireMoment}
          />
        </TabPanel>
        <TabPanel
          index={1}
          value={tabSelected}
        >
          <ScheduledMessagesList userId={userId} />
        </TabPanel>
      </ModalBody>
    </DialogRoundedModal>
  );
};

CoachReminderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDidDismiss: PropTypes.func.isRequired,
  showReminderScheduled: PropTypes.func.isRequired,
  showReminderError: PropTypes.func.isRequired,
  selectedSendDate: PropTypes.string,
};

CoachReminderModal.defaultProps = {
  selectedSendDate: null,
};

export default CoachReminderModal;
